import { useEffect, useState } from 'react';

import { describeStatus } from '../core/sync/status.js';
import { Check, Save } from './icons.js';
import { useAppStore } from '../state/store.js';

/**
 * Индикатор состояния синхронизации. Показывает ровно то, что есть: кнопки
 * «Сохранить» в продукте нет, и единственный способ узнать, что правка ещё не
 * уехала, — этот индикатор (принцип 5 требований).
 */
export function SyncStatus({
  onRejected,
  label = 'Синхронизация',
  compact = false,
}: {
  onRejected?: () => void;
  /**
   * Имя области. Индикаторов на экране может оказаться два — в подвале
   * страницы и в открытой карточке, — а две живые области с одинаковым именем
   * в озвучке неразличимы.
   */
  label?: string;
  /**
   * Показывать спокойные состояния значком, а не словом.
   *
   * Значками показываются два состояния, которые сменяют друг друга при каждой
   * правке: «сохраняем…» — дискета, «сохранено» — галочка. Словами они меняли
   * ширину, и подвал карточки дёргался на каждое нажатие клавиши.
   *
   * Всё остальное — «оффлайн», «ошибка», «правки не приняты» — остаётся
   * текстом: значок вместо них означал бы, что о непорядке сообщают намёком.
   * Подвал при этом сдвинется, и это правильно: беда обязана привлекать
   * внимание, а не прятаться в ту же ширину.
   */
  compact?: boolean;
}): React.ReactElement {
  const status = useAppStore((state) => state.status);
  const [elapsed, setElapsed] = useState(0);

  // Обратный отсчёт до повтора идёт в интерфейсе, а не в движке: движку он не
  // нужен. Счётчик сбрасывается на каждое новое состояние — иначе после
  // первого же повтора он застывает на нуле и обещает то, что уже случилось.
  useEffect(() => {
    setElapsed(0);
    if (status.kind !== 'error') return undefined;

    const timer = setInterval(() => setElapsed((value) => value + 1), 1_000);
    return () => clearInterval(timer);
  }, [status]);

  const text =
    status.kind === 'error'
      ? describeRetry(status.retryInSeconds - elapsed)
      : describeStatus(status);

  if (compact && (status.kind === 'saved' || status.kind === 'saving')) {
    return (
      <span
        className={`sync sync--${status.kind} sync--mark`}
        role="status"
        aria-live="polite"
        aria-label={`${label}: ${text}`}
        title={text}
      >
        {status.kind === 'saved' ? <Check /> : <Save />}
      </span>
    );
  }

  // Отвергнутая правка — единственное состояние, из которого есть что делать:
  // остальные проходят сами. Поэтому только оно и становится кнопкой; счётчик,
  // на который нельзя нажать, оставлял человека наедине с числом.
  if (status.kind === 'rejected' && onRejected !== undefined) {
    return (
      <button
        type="button"
        className="sync sync--rejected sync--action"
        aria-label={`${label}: ${text}. Посмотреть, что не принято`}
        onClick={onRejected}
      >
        {text}
      </button>
    );
  }

  return (
    <span
      className={`sync sync--${status.kind}`}
      role="status"
      aria-live="polite"
      aria-label={label}
    >
      {text}
    </span>
  );
}

/**
 * Досчитав до нуля, индикатор перестаёт обещать срок и говорит, что делает.
 * Повтор к этому моменту уже в пути, и «повтор через 0 с» — неправда о
 * будущем вместо правды о настоящем.
 */
function describeRetry(secondsLeft: number): string {
  return secondsLeft > 0 ? `ошибка, повтор через ${String(secondsLeft)} с` : 'ошибка, повторяем…';
}
